import { GlButton, GlCollapsibleListbox, GlIcon, GlLink, GlLoadingIcon, GlTooltipDirective } from '@gitlab/ui';
import { kebabCase, snakeCase } from 'lodash-es';
import { createAlert } from '~/alert';
import { getIdFromGraphQLId } from '~/graphql_shared/utils';
import { TYPE_ISSUE, TYPE_MERGE_REQUEST, NAMESPACE_PROJECT } from '~/issues/constants';
import { timeFor } from '~/lib/utils/datetime_utility';
import { __ } from '~/locale';
import { sanitize } from '~/lib/dompurify';
import { keysFor } from '~/behaviors/shortcuts/keybindings';
import { keyboardShortcutsDisabled } from '~/behaviors/shortcuts/shortcuts_disabled';
import { dropdowni18nText, IssuableAttributeState, IssuableAttributeType, IssuableAttributeTypeKeyMap, LocalizedIssuableAttributeType, noAttributeId, Tracking } from 'ee_else_ce/sidebar/constants';
import { issuableAttributesQueries } from 'ee_else_ce/sidebar/queries/constants';
import { glSlotsMixin } from '~/lib/utils/vue3compat/gl_slots_mixin';
export default {
    name: 'SidebarDropdownWidget',
    i18n: {
        expired: __('(expired)'),
        none: __('None'),
        edit: __('Edit')
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    components: {
        GlButton,
        GlCollapsibleListbox,
        GlIcon,
        GlLink,
        GlLoadingIcon
    },
    mixins: [
        glSlotsMixin
    ],
    inject: {
        canUpdate: {
            default: false
        },
        isClassicSidebar: {
            default: false
        },
        issuableAttributesQueries: {
            default: issuableAttributesQueries
        },
        issuableAttributesState: {
            default: IssuableAttributeState
        },
        widgetTitleText: {
            default: {
                [IssuableAttributeType.Milestone]: __('Milestone'),
                expired: __('(expired)'),
                none: __('None')
            }
        },
        keybinding: {
            default: null
        }
    },
    props: {
        issuableAttribute: {
            type: String,
            required: true
        },
        workspacePath: {
            required: true,
            type: String
        },
        iid: {
            required: true,
            type: String
        },
        attrWorkspacePath: {
            required: true,
            type: String
        },
        issuableType: {
            type: String,
            required: true,
            validator (value) {
                return [
                    TYPE_ISSUE,
                    TYPE_MERGE_REQUEST
                ].includes(value);
            }
        },
        icon: {
            type: String,
            required: false,
            default: undefined
        },
        groupBy: {
            type: Function,
            required: false,
            default: null
        }
    },
    emits: [
        'attribute-updated'
    ],
    apollo: {
        issuable: {
            query () {
                return this.issuableAttributeQuery.current[this.issuableType].query;
            },
            variables () {
                return {
                    fullPath: this.workspacePath,
                    iid: this.iid
                };
            },
            update (data) {
                return data.namespace?.issuable || {};
            },
            error (error) {
                createAlert({
                    message: this.i18n.currentFetchError,
                    captureError: true,
                    error
                });
            },
            subscribeToMore: {
                document () {
                    return issuableAttributesQueries[this.issuableAttribute].subscription;
                },
                variables () {
                    return {
                        issuableId: this.issuable.id
                    };
                },
                skip () {
                    return !issuableAttributesQueries[this.issuableAttribute].subscription;
                }
            }
        },
        attributesList: {
            query () {
                const { query } = this.issuableAttributeQuery.list[this.issuableType];
                return query[NAMESPACE_PROJECT] || query;
            },
            variables () {
                return {
                    fullPath: this.attrWorkspacePath,
                    title: this.searchTerm,
                    state: this.issuableAttributesState[this.issuableAttribute]
                };
            },
            update: (data)=>data?.namespace?.attributes?.nodes ?? [],
            result () {
                this.hasLoadedAttributes = true;
            },
            error (error) {
                this.hasLoadedAttributes = true;
                createAlert({
                    message: this.i18n.listFetchError,
                    captureError: true,
                    error
                });
            },
            skip () {
                return this.skipQuery;
            },
            debounce: 250
        }
    },
    data () {
        return {
            updating: false,
            selectedTitle: null,
            issuable: {},
            attributesList: [],
            searchTerm: '',
            skipQuery: true,
            hasLoadedAttributes: false,
            tracking: {
                event: Tracking.editEvent,
                label: Tracking.rightSidebarLabel,
                property: this.issuableAttribute
            }
        };
    },
    computed: {
        currentAttribute () {
            return this.issuable.attribute;
        },
        issuableAttributeQuery () {
            return this.issuableAttributesQueries[this.issuableAttribute];
        },
        attributeTitle () {
            return this.currentAttribute?.title || __('None');
        },
        attributeUrl () {
            return this.currentAttribute?.webUrl;
        },
        loading () {
            return this.$apollo.queries.issuable.loading;
        },
        attributeTypeTitle () {
            return this.widgetTitleText[this.issuableAttribute];
        },
        attributeTypeIcon () {
            return this.icon || this.issuableAttribute;
        },
        tooltipText () {
            return timeFor(this.currentAttribute?.dueDate);
        },
        i18n () {
            const localizedAttribute = LocalizedIssuableAttributeType[IssuableAttributeTypeKeyMap[this.issuableAttribute]];
            return dropdowni18nText(localizedAttribute, this.issuableType);
        },
        formatIssuableAttribute () {
            return {
                kebab: kebabCase(this.issuableAttribute),
                snake: snakeCase(this.issuableAttribute)
            };
        },
        isMergeRequest () {
            return this.issuableType === TYPE_MERGE_REQUEST;
        },
        supportsPopover () {
            return this.issuableAttribute === IssuableAttributeType.Milestone;
        },
        popoverAttributes () {
            if (!this.supportsPopover || !this.currentAttribute?.id) return {};
            return {
                'data-reference-type': IssuableAttributeType.Milestone,
                'data-placement': 'left',
                'data-milestone': getIdFromGraphQLId(this.currentAttribute.id)
            };
        },
        items () {
            if (this.groupBy) {
                return this.groupBy(this.attributesList);
            }
            return this.attributesList.map((attr)=>({
                    ...attr,
                    value: attr.id,
                    text: attr.title,
                    expired: this.isAttributeOverdue(attr)
                }));
        },
        flatItems () {
            if (!this.groupBy) return this.items;
            return this.items.flatMap((group)=>group.options || []);
        },
        selectedAttributeId () {
            return this.currentAttribute?.id ?? null;
        },
        searching () {
            if (this.skipQuery) return false;
            return !this.hasLoadedAttributes || this.$apollo.queries.attributesList.loading;
        },
        labelShortcutDescription () {
            return keyboardShortcutsDisabled() || !this.keybinding ? null : this.keybinding.description;
        },
        labelShortcutKey () {
            return keyboardShortcutsDisabled() || !this.keybinding ? null : keysFor(this.keybinding)[0];
        },
        labelTooltip () {
            const description = this.labelShortcutDescription;
            const key = this.labelShortcutKey;
            return keyboardShortcutsDisabled() || !this.keybinding ? null : sanitize(`${description} <kbd class="flat gl-ml-1" aria-hidden=true>${key}</kbd>`);
        }
    },
    methods: {
        isAttributeOverdue (attribute) {
            return this.issuableAttribute === IssuableAttributeType.Milestone ? attribute?.expired : false;
        },
        onSearch (value) {
            this.searchTerm = value;
        },
        onShown () {
            this.skipQuery = false;
        },
        onReset () {
            this.selectedTitle = null;
            this.$refs.dropdown.close();
            this.updateAttribute({
                id: noAttributeId
            });
        },
        onSelect (id) {
            const attr = this.flatItems.find((item)=>item.value === id);
            this.selectedTitle = attr?.text ?? this.$options.i18n.none;
            this.updateAttribute({
                id
            });
        },
        updateAttribute ({ id }) {
            if (!this.canUpdate) return;
            if (this.currentAttribute === null && id === null) return;
            if (id === this.currentAttribute?.id) return;
            this.updating = true;
            const { current } = this.issuableAttributeQuery;
            const { mutation } = current[this.issuableType];
            this.$apollo.mutate({
                mutation,
                variables: {
                    fullPath: this.workspacePath,
                    attributeId: this.issuableAttribute === IssuableAttributeType.Milestone && this.issuableType === TYPE_ISSUE ? getIdFromGraphQLId(id) : id,
                    iid: this.iid
                }
            }).then(({ data })=>{
                if (data.issuableSetAttribute?.errors?.length) {
                    createAlert({
                        message: data.issuableSetAttribute.errors[0],
                        captureError: true,
                        error: data.issuableSetAttribute.errors[0]
                    });
                } else {
                    this.$emit('attribute-updated', data);
                }
            }).catch((error)=>{
                createAlert({
                    message: this.i18n.updateError,
                    captureError: true,
                    error
                });
            }).finally(()=>{
                this.updating = false;
                this.selectedTitle = null;
            });
        }
    }
};
